import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { Link, useNavigate } from "react-router-dom";
import {
  getCheckEmail,
  getCompanyList,
  postRegister,
} from "../api/userManagementAPI";
import { useCustomAlertStore } from "../Store/store";
import {
  Main,
  Container,
  Header,
  Form,
  InputGroup,
  CheckboxGroup,
  ErrorMessage,
  Button,
  LoginLink,
  LoadingSpinner,
} from "./RegisterPage/RegisterPage.styles";

export default function RegisterPage() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    confirmPassword: "",
    name: "",
    phone: "",
    company: "",
    privacy: false,
  });

  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [companyList, setCompanyList] = useState([]);
  const { showSuccessAlert, showErrorAlert, showInfoAlert } =
    useCustomAlertStore();

  // 회사 목록 조회
  useEffect(() => {
    const fetchCompanyList = async () => {
      try {
        const response = await getCompanyList();
        setCompanyList([{ id: 0, name: "회사를 선택해주세요" }, ...response]);
      } catch (error) {
        console.error("회사 목록 조회 실패:", error);
      }
    };
    fetchCompanyList();
  }, []);

  // 폼 검증
  const validateForm = () => {
    const newErrors = {};

    // 이메일 검증
    if (!formData.email) {
      newErrors.email = "이메일을 입력해주세요.";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "올바른 이메일 형식을 입력해주세요.";
    }

    // 비밀번호 검증
    if (!formData.password) {
      newErrors.password = "비밀번호를 입력해주세요.";
    } else if (formData.password.length < 8) {
      newErrors.password = "비밀번호는 8자 이상이어야 합니다.";
    }

    // 비밀번호 확인 검증
    if (!formData.confirmPassword) {
      newErrors.confirmPassword = "비밀번호 확인을 입력해주세요.";
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = "비밀번호가 일치하지 않습니다.";
    }

    // 이름 검증
    if (!formData.name) {
      newErrors.name = "이름을 입력해주세요.";
    }

    // 전화번호 검증
    if (!formData.phone) {
      newErrors.phone = "전화번호를 입력해주세요.";
    } else if (!/^[0-9-+\s()]+$/.test(formData.phone)) {
      newErrors.phone = "올바른 전화번호 형식을 입력해주세요.";
    }

    // 회사 검증
    if (!formData.company) {
      newErrors.company = "회사를 선택해주세요.";
    } else if (formData.company === "0") {
      newErrors.company = "회사를 선택해주세요.";
    }

    // 개인정보 수집 동의 검증
    if (!formData.privacy) {
      newErrors.privacy = "개인정보 수집에 동의해주세요.";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // 입력 핸들러
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));

    // 실시간 에러 제거
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  // 회원가입 핸들러
  const handleSubmit = async (e) => {
    e.preventDefault();

    // 폼 검증 실패 시 오류 메시지 표시
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      const response = await postRegister(formData);

      if (response.success) {
        // 바로 로그인 페이지로 이동 (모달 없이)
        navigate("/login");
      } else {
        showErrorAlert(
          "회원가입 실패",
          response.message || "회원가입에 실패했습니다.",
        );
      }
    } catch (error) {
      console.error("회원가입 에러:", error);
      showErrorAlert("회원가입 오류", "회원가입 중 오류가 발생했습니다.");
    } finally {
      setIsLoading(false);
    }
  };

  const isFormValid =
    Object.values(formData).every((value) =>
      typeof value === "boolean" ? value : value.trim() !== "",
    ) && Object.keys(errors).length === 0;

  const handleCheckEmail = async (e) => {
    e.preventDefault();
    if (!formData.email) {
      showErrorAlert("입력 오류", "이메일을 입력해주세요.");
      return;
    }
    try {
      const response = await getCheckEmail(formData.email);
      if (response.success) {
        showInfoAlert("이메일 확인", "사용 가능한 이메일입니다.");
      } else {
        showErrorAlert("이메일 확인", "이미 사용 중인 이메일입니다.");
      }
    } catch (error) {
      console.error("이메일 중복확인 실패:", error);
      showErrorAlert("확인 오류", "이메일 중복확인에 실패했습니다.");
    }
  };

  return (
    <Main>
      <Container>
        <Header className="project-header">
          <h1>회원가입</h1>
          <p>회원가입을 위한 정보를 입력해주세요.</p>
        </Header>
        <Form onSubmit={handleSubmit}>
          <InputGroup error={errors.email}>
            <label htmlFor="email">이메일 *</label>
            <div className="email-input-group">
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                placeholder="example@email.com"
              />
              <button type="button" onClick={handleCheckEmail}>
                중복확인
              </button>
            </div>
            {errors.email && <ErrorMessage>{errors.email}</ErrorMessage>}
          </InputGroup>

          <InputGroup error={errors.password}>
            <label htmlFor="password">비밀번호 *</label>
            <input
              type="password"
              id="password"
              name="password"
              value={formData.password}
              onChange={handleInputChange}
              placeholder="8자 이상 입력해주세요"
            />
            {errors.password && <ErrorMessage>{errors.password}</ErrorMessage>}
          </InputGroup>

          <InputGroup error={errors.confirmPassword}>
            <label htmlFor="confirmPassword">비밀번호 확인 *</label>
            <input
              type="password"
              id="confirmPassword"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleInputChange}
              placeholder="비밀번호를 다시 입력해주세요"
            />
            {errors.confirmPassword && (
              <ErrorMessage>{errors.confirmPassword}</ErrorMessage>
            )}
          </InputGroup>

          <InputGroup error={errors.name}>
            <label htmlFor="name">이름 *</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              placeholder="홍길동"
            />
            {errors.name && <ErrorMessage>{errors.name}</ErrorMessage>}
          </InputGroup>

          <InputGroup error={errors.phone}>
            <label htmlFor="phone">전화번호 *</label>
            <input
              type="tel"
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={handleInputChange}
              placeholder="010-1234-5678"
            />
            {errors.phone && <ErrorMessage>{errors.phone}</ErrorMessage>}
          </InputGroup>

          <InputGroup error={errors.company}>
            <label htmlFor="company">회사명 *</label>
            <select
              name="company"
              id="company"
              value={formData.company}
              onChange={handleInputChange}
            >
              {companyList.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.name}
                </option>
              ))}
            </select>
            {errors.company && <ErrorMessage>{errors.company}</ErrorMessage>}
          </InputGroup>

          <CheckboxGroup error={errors.privacy}>
            <input
              type="checkbox"
              id="privacy"
              name="privacy"
              checked={formData.privacy}
              onChange={handleInputChange}
            />
            <label htmlFor="privacy">
              <span>개인정보 수집에 동의합니다.</span>
              <a href="/privacy" target="_blank" rel="noopener noreferrer">
                (개인정보처리방침 보기)
              </a>
            </label>
          </CheckboxGroup>
          {errors.privacy && <ErrorMessage>{errors.privacy}</ErrorMessage>}

          <Button type="submit" disabled={!isFormValid || isLoading}>
            {isLoading ? (
              <>
                <LoadingSpinner /> 처리중...
              </>
            ) : (
              "회원가입"
            )}
          </Button>
        </Form>

        <LoginLink>
          이미 계정이 있으신가요? <Link to="/login">로그인하기</Link>
        </LoginLink>
        <LoginLink>
          원하는 기관이 없으신가요?{" "}
          <Link to="/company-request">기관/기업명 추가 요청</Link>
        </LoginLink>
      </Container>
    </Main>
  );
}
